import { useEffect, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Text,
  Stack,
  Collapse,
  Button, 
  Group,
  Select,
  Paper,
  TextInput,
  Switch,
  Radio,
} from '@mantine/core';
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { SaveResponses, GetVerificarHomologacion } from "../actions/ResponsesActions";
import { Row,Col} from 'react-bootstrap';
import { GetPaises, GetTipoSolicitudEstudiante } from "../actions/FormDataActions"
import useFormTracking from "../hooks/useFormTracking";

const HomologationForm = forwardRef(({ stepNumber, t, id }, ref) => {

  const dispatch = useDispatch();
  const response = useSelector((state) => state.respuestas.steps[stepNumber]?.responses || {});
  const { data: dataPaises } = useSelector((state) => state.datosFormulario.paises);
  const { data: dataTiposSol } = useSelector((state) => state.datosFormulario.tiposSolicitudes);
  const homologacion = useSelector((state) => state.respuestas.homologacion);
  const { track } = useFormTracking(stepNumber);
  const trackSelect = (fieldName) => (value) => {
    form.setFieldValue(fieldName, value);
    if (value) track('field_completed', { fieldName });
  };
  
  // Obtener datos del programa y persona para la validación
  const programaStep = useSelector((state) => state.respuestas.steps[1]?.responses || {});
  const personaStep = useSelector((state) => state.respuestas.steps[2]?.responses || {});
  const contactoStep = useSelector((state) => state.respuestas.steps[3]?.responses || {});

  
  const getValidations = (form) => {
    const validations = {
      catAlumno: (value) => (value ? null : "El campo es obligatorio"),
      paisTitulo: (value) => (value ? null : "El campo es obligatorio"),
    };
  
    if (response.solicitaPreinforme && response.estadoPreinforme === 'poseo') {
      validations.preinforme = (value) => (value.length > 1 ? null : "El campo es obligatorio");
    }
  
    return validations;
  };

  const form = useForm({
    initialValues: {
      solicitaPreinforme: response.solicitaPreinforme || false,
      estadoPreinforme: response.estadoPreinforme || null,
      preinforme: response.preinforme || '',
      preinformeValid: response.preinformeValid || null,
      catAlumno: response.catAlumno || null,
      paisTitulo: response.paisTitulo || 'AR',
      declaracion: response.declaracion || false,
    },
    validate: getValidations(),
  });
  
  const [debouncedValues] = useDebouncedValue(form.values, 300);
    
  useEffect(() => {
    dispatch(SaveResponses(id, debouncedValues));
  }, [debouncedValues, dispatch, id]);

  useEffect(() => {
    dispatch(GetPaises())
    dispatch(GetTipoSolicitudEstudiante())
  }, [dispatch]);

  useEffect(() => {
    if (dataTiposSol?.length && !form.values.catAlumno) {
      const regular = dataTiposSol.find((t) => t.label === 'Regular');
      if (regular) form.setFieldValue('catAlumno', regular.value);
    }
  }, [dataTiposSol]);

  useEffect(() => {
    if (homologacion.isValid === true)  track('field_completed', { fieldName: 'preinforme_validated' });
    if (homologacion.isValid === false) track('field_error',     { fieldName: 'preinforme_validated' });
  }, [homologacion.isValid]);

  // Función para validar preinforme
  const handleValidarPreinforme = () => {
    if (!form.values.preinforme) {
      return;
    }

    // Obtener datos necesarios para la validación
    const carrera = programaStep.carrera?.split('-')[0]?.trim();
    const plan = programaStep.carrera?.split('-')[1]?.trim();
    const tipodoc = personaStep.documentoTipo;
    const numerodoc = personaStep.documentoNumero;
    const email = contactoStep.email;
    const nro_preinforme = form.values.preinforme;

    if (carrera && plan && tipodoc && numerodoc && email && nro_preinforme) {
      track('field_completed', { fieldName: 'preinforme_validation_started' });
      dispatch(GetVerificarHomologacion(carrera, plan, tipodoc, numerodoc, email, nro_preinforme));
    }
  };

  // Validación expuesta al componente padre
  useImperativeHandle(ref, () => ({
    validate: () => {
      const validation = form.validate();
      if (validation.hasErrors) {
        Object.keys(validation.errors).forEach((field) =>
          track('field_error', { fieldName: field })
        );
        return false;
      }
      return true;
    },
  }));

return (
  <Container size="100%">
    <Text size="xl" fw={500} mb={'xs'} c={'rgb(85, 85, 85)'}>Preinforme de Homologaciones</Text>
    <Row className="row-inputs">
      <Col md="5">
        <Text style={{ marginBottom: '10px' }} size="sm">
          Solicito preinforme de equivalencias de asignaturas aprobadas en instancia de final, en otras instituciones
        </Text>
        <Switch
          color="pink"
          size="lg"
          onLabel="Si"
          offLabel="No"
          //onChange={(value) => handleSaveResponse('solicitaPreinforme',value.currentTarget.checked)}
          {...form.getInputProps("solicitaPreinforme",{ type: 'checkbox' })}
          onChange={(e) => {
            form.setFieldValue('solicitaPreinforme', e.currentTarget.checked);
            track('field_completed', { fieldName: 'solicitaPreinforme' });
          }}
        />
      </Col>
      <Col md="7">
        {/* Mostrar u ocultar el panel derecho basado en el estado del switch */}
        <Collapse in={form.values.solicitaPreinforme} transitionDuration={300} transitionTimingFunction="linear">
          <Paper shadow="xs" radius="md" p="lg" >
            <Stack align="stretch" justify="center" gap="xl">
              <Radio.Group
                name="estadoPreinforme"
                //onChange={(value) => handleSaveResponse('estadoPreinforme',value)}
                {...form.getInputProps("estadoPreinforme")}
                onChange={(value) => {
                  form.setFieldValue('estadoPreinforme', value);
                  if (value) track('field_completed', { fieldName: 'estadoPreinforme' });
                }}
              >
                <Group>
                  <Radio value="posee" label="Ya poseo un preinforme" color="pink" />
                  <Radio value="solicita" label="Deseo solicitar un nuevo preinforme" color="pink" />
                </Group>
              </Radio.Group>
              {form.values.estadoPreinforme === 'posee' && (
                <Stack>
                  <TextInput 
                    type="number" 
                    label='Numero de Preinforme'
                    placeholder="Ingrese el numero de preinforme"
                    //onChange={(value) => handleSaveResponse('preinforme',value)}
                    {...form.getInputProps("preinforme")}
                  />
                  <Button 
                    color="pink" 
                    mt="md"
                    onClick={handleValidarPreinforme}
                    loading={homologacion.loading}
                    disabled={!form.values.preinforme || homologacion.loading}
                  >
                    {homologacion.loading ? 'Validando...' : 'Validar'}
                  </Button>
                  
                  {/* Feedback visual */}
                  {homologacion.isValid === true && (
                    <Text size="sm" c="green" fw={500}>
                      ✅ Preinforme válido
                    </Text>
                  )}
                  {homologacion.isValid === false && (
                    <Text size="sm" c="red" fw={500}>
                      ❌ Preinforme inválido
                    </Text>
                  )}
                  {homologacion.error && (
                    <Text size="sm" c="red">
                      Error: {homologacion.error}
                    </Text>
                  )}
                </Stack>
              )}
              {form.values.estadoPreinforme === 'solicita' && (
                <Text size="sm">
                  Al confirmar tu correo electrónico recibirás un link para la carga de la solicitud de preinforme
                </Text>
              )}
            </Stack>
          </Paper>
        </Collapse>
      </Col>
    </Row>
    <Text size="xl" fw={500} mb={'xs'} mt={'xl'} c={'rgb(85, 85, 85)'}>Informe Complementario</Text>
    <Row className='row-inputs'>
      <Col md='5'>
        <Select
          required
          label="Categoría de alumno"
          placeholder="Seleccione una categoria"
          data={dataTiposSol}
          //onChange={(value) => handleSaveResponse('catAlumno',value)}
          {...form.getInputProps("catAlumno")}
          onChange={trackSelect('catAlumno')}
        />
      </Col>
      <Col md='7'>    
        <Text size='sm' mt={20} c={'dimmed'}>Categoría Regular: Son los que se inscriben en una o más de las carreras de grado, posgrado, intermedias o cortas que ofrece la Universidad, se ajustan a las condiciones de cursado que establezca el Plan de Estudios correspondiente y cumplen con las demás obligaciones fijadas.</Text>
      </Col>
    </Row>
    <Row className='row-inputs'>
      <Col md='5'>
        <Select
          required
          label="País que expide el último título obtenido"
          placeholder="Seleccione país"
          searchable
          data={dataPaises}
          //onChange={(value) => handleSaveResponse('paisTitulo',value)}
          {...form.getInputProps("paisTitulo")}
          onChange={trackSelect('paisTitulo')}
        />
      </Col>
    </Row>
    <Row className='row-inputs' style={{marginTop:'2rem'}}>
      <Col md='12'>
        { t === 'g' && (
          <>
            <Text size='sm'>Declaro no haber finalizado estudios secundarios siendo mayor de 25 años y solicito el ingreso aceptando la matriculación condicional hasta aprobar el examen que establece la Universidad.</Text>
            <Switch
              color="pink"
              size="lg"
              onLabel="Si"
              offLabel="No"
              mt={10}
              checked={form.values.declaracion}
              //onChange={(value) => handleSaveResponse('declaracion',value.currentTarget.checked)}
              {...form.getInputProps("declaracion")}
              onChange={(e) => {
                form.setFieldValue('declaracion', e.currentTarget.checked);
                track('field_completed', { fieldName: 'declaracion' });
              }}
            />
          </>
        )}
      </Col>
    </Row>
  </Container>
);
});

export default HomologationForm;